import React from 'react';
import {useToast} from '@/hooks/use-toast';
import {useAuth} from "@/contexts/AuthContext";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {LoginForm} from '@/components/signin/LoginForm';
import CONFIG from '@/lib/config';
import { safeRedirectPath } from '@/lib/redirect';

// This page is mounted at /admin only (see App.tsx). Candidates sign up through
// the landing page's ProfileJourneyModal and employers through ContactCTA — the
// separate SignupForm that used to live here was reachable solely via
// /admin?reg=true, still carried "OSÄKER PÅ HUR MAN SKA HANTERA 403..." as its
// error copy, and told new accounts they could log in immediately. It is gone
// rather than repaired: a second signup form on the admin portal is not a
// surface this product wants.
const Login: React.FC = () => {
    const {toast} = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const {logout} = useAuth();

    // /admin is admin-only.
    const isAdminPath = location.pathname === '/admin';

    const handleLoginSuccess = async (success: boolean, user?: { role?: string }) => {
        if (!success) {
            toast({
                title: "Fel vid inloggning",
                description: "Felaktig e-postadress eller lösenord.",
            });
            return;
        }

        const userRole = user?.role ?? '';

        // /admin is strictly admin-only regardless of which other roles are
        // allowed in the broader app. Regular users log in via the landing
        // page slide-over.
        if (isAdminPath && userRole !== 'admin') {
            await logout();
            toast({
                title: "Inloggning inte tillåten",
                description: "Den här inloggningen är endast tillgänglig för administratörer.",
                variant: "destructive",
            });
            return;
        }

        // Otherwise apply the broader role allowlist (covers admin-only soft
        // launches where even admins land here, and any future general /login).
        if (!CONFIG.ALLOWED_ROLES.has(userRole)) {
            await logout();
            toast({
                title: "Inloggning inte tillåten",
                description: "Plattformen är inte öppen för din användartyp just nu.",
                variant: "destructive",
            });
            return;
        }

        toast({
            title: "Inloggning lyckades",
            description: "Välkommen tillbaka!",
        });
        // #265 — honour a safe ?redirect= (e.g. back to a shared job posting).
        navigate(safeRedirectPath(searchParams.get('redirect')) ?? '/dashboard');
    };

    return (
        <div className="min-h-screen flex  flex-col"> {/* bg-[#1A4D4F] */}
            <header className="flex justify-between items-center px-6 sm:px-12 md:px-24 py-4 w-full border-b">
                <div className="flex-1 flex w-full items-center justify-center">
                    <img
                        loading="lazy"
                        src="/images/e937596a79e27aedd1ca37344ef2209cd6e8c5da2cc900e6c0f5467b6176325e.png"
                        alt="Nexilink Logo"
                        className="w-[150px] sm:w-[206px] object-contain aspect-[2.61]"
                        onClick={() => navigate('/')}
                        style={{cursor: 'pointer'}}
                    />
                </div>
            </header>

            <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
                <div className="w-full max-w-[600px] rounded-lg drop-shadow-2xl border border-gray-200">
                    <LoginForm
                        onLoginCallback={handleLoginSuccess}
                        // Admin login: no register link, no Google (OAuth flow
                        // bypasses our role gate, so password is the only path).
                        disableSocialLogin={isAdminPath}
                        title={isAdminPath ? 'Adminportalen' : undefined}
                        subtitle={isAdminPath ? 'Logga in för att hantera plattformen' : undefined}
                    />
                </div>
            </main>

            <footer className="py-6 text-center text-neutral-100">
                <p>© {new Date().getFullYear()} Nexilink. Alla rättigheter förbehållna.</p>
            </footer>
        </div>
    );
};

export default Login;